import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Video, Upload, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

const CHUNK = 2 * 1024 * 1024;
const MAX = 150 * 1024 * 1024;

export default function VideoConfig({ form, setForm }) {
  const [progress, setProgress] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX) return toast.error("Vídeo muito grande (máx. 150 MB).");
    if (!/\.(mp4|m4v|mov|webm)$/i.test(file.name)) return toast.error("Envie um vídeo MP4 ou WEBM.");
    const uploadId = crypto.randomUUID();
    const total = Math.ceil(file.size / CHUNK);
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
        await api.post("/admin/video/chunk", fd);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      await api.post("/admin/video/finalizar", { upload_id: uploadId, filename: file.name });
      setForm((f) => ({ ...f, tem_video: true, video_url: "" }));
      toast.success("Vídeo enviado! Ligue a chave e salve as configurações para exibir.");
    } catch (e) {
      toast.error(formatApiError(e, "Falha ao enviar o vídeo."));
    } finally {
      setProgress(null);
    }
  };

  const remover = async () => {
    if (!window.confirm("Remover o vídeo enviado?")) return;
    try {
      await api.delete("/admin/video");
      setForm((f) => ({ ...f, tem_video: false }));
      toast.success("Vídeo removido.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="video-config" className="sm:col-span-2 bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Video className="w-5 h-5 text-[#0f2a6b] mt-0.5 shrink-0" />
          <div>
            <Label className="text-base font-semibold text-slate-800">Vídeo de apresentação</Label>
            <p className="text-sm text-slate-500">Aparece no topo da página pública, logo abaixo do título. Ligue a chave para exibir.</p>
          </div>
        </div>
        <Switch data-testid="config-video_ativo" checked={!!form.video_ativo} onCheckedChange={(v) => setForm((f) => ({ ...f, video_ativo: v }))} />
      </div>
      <div>
        <Label className="text-sm font-semibold text-slate-700">Título do vídeo (opcional)</Label>
        <Input data-testid="config-video_titulo" value={form.video_titulo || ""} onChange={set("video_titulo")} placeholder="Ex.: Uma palavra do pastor" className="mt-1.5 h-11 text-base rounded-xl bg-white" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-semibold text-slate-700">Enviar vídeo (MP4/WEBM, até 150 MB)</Label>
          <label htmlFor="video-upload" className={`mt-1.5 flex items-center justify-center gap-2 h-11 rounded-xl border-2 border-dashed text-sm cursor-pointer transition-colors bg-white ${progress !== null ? "border-blue-300 text-blue-800" : "border-slate-300 hover:border-blue-400 text-slate-600"}`}>
            {progress !== null ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando {progress}%</> : form.tem_video ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Vídeo enviado — trocar</> : <><Upload className="w-4 h-4" /> Escolher vídeo</>}
          </label>
          <input id="video-upload" data-testid="config-video-upload" type="file" accept="video/mp4,video/webm,.mp4,.mov,.m4v,.webm" className="hidden" disabled={progress !== null} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          {progress !== null && (
            <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-blue-700 transition-[width]" style={{ width: `${progress}%` }} /></div>
          )}
          {form.tem_video && progress === null && (
            <button type="button" data-testid="config-video-remove" onClick={remover} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-700">
              <Trash2 className="w-3.5 h-3.5" /> Remover vídeo enviado
            </button>
          )}
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700">Ou link do YouTube</Label>
          <Input data-testid="config-video_url" value={form.video_url || ""} onChange={set("video_url")} placeholder="https://youtube.com/watch?v=..." className="mt-1.5 h-11 text-base rounded-xl bg-white" />
          <p className="mt-1 text-xs text-slate-400">Se preencher o link, ele tem prioridade sobre o vídeo enviado.</p>
        </div>
      </div>
    </div>
  );
}
